import { DEFAULT_ENDPOINT } from './constants';

type gqlFetchProps = {
  query?: string;
  variables?: Record<string, unknown>;
  headers?: Record<string, string>;
  tags?: string[];
  cache?: RequestCache;
  endpoint?: string;
  next?: {
    tags?: string[];
    revalidate?: number;
  };
};

export async function gqlFetch({
  endpoint = DEFAULT_ENDPOINT,
  query,
  variables,
  headers,
  cache,
  next,
}: gqlFetchProps) {
  let retryCount = 0;
  const maxRetries = 5;
  let delay = 5000; // 5 seconds


  while (retryCount < maxRetries) {
    try {
      const result = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          ...(query && { query }),
          ...(variables && { variables }),
        }),
        cache,
        next,
      });

      if (result.status === 429) {
        throw new Error('Too Many Requests');
      }

      if (result.status === 504) {
        throw new Error('GraphQL timeout error');
      }

      if (!result.ok) {
        const queryName = query?.match(/query\s+(\w+)/)?.[1];
        console.log('Error:', result.status, queryName, endpoint);

        return {
          data: {},
          status: result.status,
        };
      }

      const body = await result.json();

      if (body.errors) {
        throw body.errors[0];
      }

      return {
        data: body.data,
        status: result.status,
      };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === 'Too Many Requests' ||
          error.message === 'GraphQL timeout error')
      ) {
        retryCount++;
        delay *= 2;
        console.log('Retrying in', delay, 'ms');
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else if (isErrorMsgObj(error)) {
        throw error.message;
      } else {
        throw 'Uh oh, something went wrong';
      }
    }
  }

  throw new Error('Failed to fetch data after multiple retries');
}

function isErrorMsgObj<T>(obj: T): obj is T & { message: unknown } {
  // @ts-expect-error ignore error
  return obj && 'message' in obj;
}
